import { isRecoverableFirestoreError } from './firestoreRecovery';

/**
 * Sentry (apps/user) — フロントエンドエラー収集 (docs/14 §3、docs/98 R-69)。
 *
 * **稼働条件**: `PROD` または `MODE === 'staging'` かつ `VITE_SENTRY_DSN` が設定されている時のみ init。
 * それ以外 (dev / DSN 未設定) は **silent no-op**。`@sentry/react` は dynamic import で読み込み、
 * DSN 未設定環境では bundle にロードしない (code-split)。
 *
 * **PII 非送信** (docs/14 §3-2 / G6 / REQ-5.5.6): `beforeSend` (= `scrubPII`) で投稿本文・ニックネーム・
 * 位置情報・写真 URL を除去し、URL に含まれる districtToken を redact する。匿名 auth のため user も送らない。
 *
 * **R-69 で特に拾う signal**:
 *   - Firestore 内部アサーション #7341 系 (`INTERNAL ASSERTION FAILED` / `already been terminated` /
 *     `not in the required state`): firebase.ts の自動復帰 (`window.location.reload()`) は Sentry の
 *     window handler より**先に発火**するため auto-capture が間に合わない。よって復帰前に
 *     `recordRecoverySignal()` で localStorage マーカーを書き、**次回起動の `initSentry()` で報告**する
 *     (reload を跨いで確実に拾う + 自動復帰の発火回数を計測。docs/14 §3-5 alert 連動)。
 *     reload を伴わない 2 回目以降の #7341 は素通しされ `beforeSend` の fingerprint でグループ化される。
 *   - 投稿失敗: outbox 送信が MAX_ATTEMPTS 超過で `status='failed'` になった時点 (`useOutbox`) で
 *     `captureException` (tag `post.send-failed`)。
 *
 * 実際の送信は DSN 設定後 (Sentry プロジェクト作成 → `VITE_SENTRY_DSN` を env/secret に登録) に有効化される。
 */

const DSN: string = import.meta.env.VITE_SENTRY_DSN ?? '';
const ENV: string = import.meta.env.MODE ?? 'development';
const RELEASE: string | undefined = import.meta.env.VITE_APP_VERSION || undefined;
const IS_PROD: boolean = import.meta.env.PROD === true;

/** localStorage キー: reload を跨いで報告する pending #7341 / 自動復帰イベント。 */
const PENDING_RECOVERY_KEY = 'cw_pending_recovery_report';

/**
 * PII を含みうるキー (docs/14 §3-2)。event の extra / contexts / breadcrumb.data から再帰的に除去する。
 * 投稿本文・ニックネーム・実名・位置情報・写真 URL/パスを対象にする。
 */
const PII_KEYS = [
  'comment',
  'authorNickname',
  'nickname',
  'realName',
  'location',
  'lat',
  'lng',
  'latitude',
  'longitude',
  'photoUrl',
  'photoPath',
  'photoThumbPath',
];

let initialized = false;

/** PROD or staging かつ DSN 設定時のみ有効 (dev では送らない、docs/14 §3-1)。 */
function shouldEnable(): boolean {
  return Boolean(DSN) && (IS_PROD || ENV === 'staging');
}

/**
 * Sentry event から PII を除去する純粋関数 (`beforeSend` 本体、test しやすいよう export)。
 *   - URL の districtToken (`/m/<token>`) を redact
 *   - extra / contexts / breadcrumb.data から既知の PII キーを除去 (深さ制限付き再帰)
 *   - 匿名 auth のため user は丸ごと削除
 *   - #7341 系は fingerprint / tag を付けてグループ化 + alert 可能に
 *
 * 引数 / 戻り値は Sentry の Event だが、@sentry に依存せず単体テストできるよう最小 shape で型付けする。
 */
export interface ScrubbableEvent {
  request?: { url?: string } | null;
  breadcrumbs?: Array<{ data?: Record<string, unknown> | null } & Record<string, unknown>> | null;
  extra?: Record<string, unknown> | null;
  contexts?: Record<string, unknown> | null;
  user?: unknown;
  exception?: { values?: Array<{ value?: string; type?: string }> } | null;
  message?: string;
  fingerprint?: string[];
  tags?: Record<string, unknown>;
}

function redactDistrictToken(url: string): string {
  // `/m/<urlToken>` の token 部を伏せる (districtToken は半 secret)。
  return url.replace(/\/m\/[^/?#]+/g, '/m/<REDACTED>');
}

function scrubObject(obj: unknown, depth = 0): void {
  if (depth > 4 || obj === null || typeof obj !== 'object') return;
  const record = obj as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (PII_KEYS.includes(key)) {
      record[key] = '[scrubbed]';
      continue;
    }
    const value = record[key];
    if (value && typeof value === 'object') scrubObject(value, depth + 1);
  }
}

/** event が #7341 系の Firestore 内部アサーションか判定 (exception value + message を結合して検査)。 */
function isFirestoreAssertionEvent(event: ScrubbableEvent): boolean {
  const parts: string[] = [];
  if (event.message) parts.push(event.message);
  for (const v of event.exception?.values ?? []) {
    if (v.value) parts.push(v.value);
    if (v.type) parts.push(v.type);
  }
  return parts.some((p) => isRecoverableFirestoreError(p));
}

export function scrubPII(event: ScrubbableEvent): ScrubbableEvent {
  if (event.request?.url) event.request.url = redactDistrictToken(event.request.url);
  for (const b of event.breadcrumbs ?? []) {
    if (typeof b.data?.url === 'string') b.data.url = redactDistrictToken(b.data.url);
    if (b.data) scrubObject(b.data);
  }
  if (event.extra) scrubObject(event.extra);
  if (event.contexts) scrubObject(event.contexts);
  // 匿名 auth: user 情報 (ip / id / nickname 等) は一切送らない。
  delete event.user;
  if (isFirestoreAssertionEvent(event)) {
    event.fingerprint = ['firestore-internal-assertion'];
    event.tags = { ...event.tags, firestore_assertion: 'true' };
  }
  // エラーメッセージ本文に URL/districtToken が紛れた場合の防御的 redact (reviewer 指摘)。
  // **前提**: 投稿本文・ニックネーム等の free-text PII は Error message に埋め込まない (docs/13 §8-3、
  // 自由文は確実な scrub 不能なため「そもそも入れない」運用)。ここで拾えるのは URL/token の混入のみ。
  if (event.message) event.message = redactDistrictToken(event.message);
  for (const v of event.exception?.values ?? []) {
    if (v.value) v.value = redactDistrictToken(v.value);
  }
  return event;
}

type SentryModule = typeof import('@sentry/react');
let sentryMod: SentryModule | null = null;

async function loadSentry(): Promise<SentryModule | null> {
  if (sentryMod) return sentryMod;
  try {
    sentryMod = await import('@sentry/react');
    return sentryMod;
  } catch (e) {
    console.warn('[Sentry] @sentry/react のロードに失敗', e);
    return null;
  }
}

/**
 * 自動復帰 (#7341) のシグナルを localStorage に記録する (firebase.ts の recoverFromAssertion から呼ぶ)。
 * reload を跨いで次回起動の initSentry で報告するため。Sentry 未 init でも安全 (storage 書込のみ)。
 */
export function recordRecoverySignal(reason: unknown): void {
  try {
    const text =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : String(reason);
    window.localStorage?.setItem(
      PENDING_RECOVERY_KEY,
      JSON.stringify({ reason: text.slice(0, 200), at: Date.now() }),
    );
  } catch {
    /* localStorage 不可でも続行 */
  }
}

/** init 後に pending な自動復帰マーカーがあれば報告して消す。 */
function reportPendingRecovery(Sentry: SentryModule): void {
  try {
    const raw = window.localStorage?.getItem(PENDING_RECOVERY_KEY);
    if (!raw) return;
    window.localStorage.removeItem(PENDING_RECOVERY_KEY);
    const data = JSON.parse(raw) as { reason?: string; at?: number };
    Sentry.captureException(
      new Error(`Firestore auto-recovery fired: ${data.reason ?? 'unknown'}`),
      {
        // fingerprint は beforeSend の #7341 検知でも付くが、自動復帰は専用 tag で区別する。
        tags: { firestore_assertion: 'true', auto_recovery: 'true' },
        extra: { recoveredAt: data.at, reason: data.reason },
      },
    );
  } catch {
    /* noop */
  }
}

/**
 * Sentry を初期化する。PROD/staging かつ DSN 設定時のみ。dev / DSN 未設定は no-op。
 * main.tsx の起動時に 1 度呼ぶ。
 */
export async function initSentry(): Promise<void> {
  if (initialized) return;
  if (!shouldEnable()) {
    console.info(
      '[Sentry] 無効 (dev または VITE_SENTRY_DSN 未設定)。PROD/staging + DSN 設定で有効化されます。',
    );
    return;
  }
  const Sentry = await loadSentry();
  if (!Sentry) return;
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    // パフォーマンストレースは Firebase Performance Monitoring の担当 (docs/14 §4)。
    // Sentry は error 収集に限定するため traces / replays は無効。
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    sendDefaultPii: false,
    beforeSend: (event) => scrubPII(event as ScrubbableEvent) as typeof event,
  });
  initialized = true;
  console.info('[Sentry] initialized', { environment: ENV, release: RELEASE });
  reportPendingRecovery(Sentry);
}

/**
 * 任意の例外を Sentry に通知する。未 init (DSN 未設定 / dev) 時は no-op。
 * 推奨用途: catch ブロック末尾、ErrorBoundary の onError、outbox 送信の確定失敗時など。
 */
export function captureException(
  err: unknown,
  context?: Record<string, unknown>,
  tags?: Record<string, string>,
): void {
  if (!initialized) return;
  void loadSentry().then((Sentry) => {
    Sentry?.captureException(err, context || tags ? { extra: context, tags } : undefined);
  });
}

/** 任意のメッセージを Sentry に通知する。未 init 時は no-op。 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
  context?: Record<string, unknown>,
): void {
  if (!initialized) return;
  void loadSentry().then((Sentry) => {
    Sentry?.captureMessage(message, { level, extra: context });
  });
}
